import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { ParsedSawEntryBarcode } from "../utils/entryBarcode";

const moment = getMomentTz();

export interface BundleQualityCheckPayload {
	qualityCheckId?: number | null;
	bundleId?: number | null;
	assetId: number;
	checkTL?: number | null;
	checkTR?: number | null;
	checkMC?: number | null;
	checkBL?: number | null;
	checkBR?: number | null;
	// Correcting a past check keeps its original time and shift.
	keepTimestamp?: boolean;
	// Tablet operator; PC leaves it null.
	employeeNumber?: string | null;
}

export const upsertBundleQualityCheck = async (
	payload: BundleQualityCheckPayload,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "QualityCheckId", value: payload.qualityCheckId ?? null },
		{ name: "BundleId", value: payload.bundleId ?? null },
		{ name: "AssetId", value: payload.assetId },
		{ name: "CheckTL", value: payload.checkTL ?? null },
		{ name: "CheckTR", value: payload.checkTR ?? null },
		{ name: "CheckMC", value: payload.checkMC ?? null },
		{ name: "CheckBL", value: payload.checkBL ?? null },
		{ name: "CheckBR", value: payload.checkBR ?? null },
		{ name: "KeepTimestamp", value: payload.keepTimestamp ?? false },
		{ name: "EmployeeNumber", value: payload.employeeNumber ?? null },
	];
	const resp = await apiService.callV2(
		"[FAB].[UpsertBundleQualityCheck]",
		parameters,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error saving bundle quality check");
	}
	return resp;
};

export const deleteBundleQualityCheck = async (
	qualityCheckId: number,
	employeeNumber: string | null = null,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "QualityCheckId", value: qualityCheckId },
		{ name: "EmployeeNumber", value: employeeNumber },
	];
	const resp = await apiService.callV2(
		"[FAB].[DeleteBundleQualityCheck]",
		parameters,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting bundle quality check");
	}
	return resp;
};

export interface SawQualityCheck {
	QualityCheckId: number;
	BundleId: number | null;
	BundleBarCount: number | null;
	BundleStartTime: Date | null;
	BundleEndTime: Date | null;
	MinLength: number | null;
	MaxLength: number | null;
	IsCutback: boolean | null;
	CheckTL: number | null;
	CheckTR: number | null;
	CheckMC: number | null;
	CheckBL: number | null;
	CheckBR: number | null;
	CheckTime: Date;
	CreatedBy: string | null;
}

export const getSawQualityChecks = async (
	assetId: number,
): Promise<SawQualityCheck[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "AssetId", value: assetId }];
	const resp = await apiService.callV2(
		"[FAB].[GetSawQualityChecks]",
		parameters,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching saw quality checks");
	}
	const rows = get(resp, "data.tables[0].rows", []) as Record<
		string,
		unknown
	>[];
	return rows.map((r) => ({
		QualityCheckId: r.QualityCheckId as number,
		BundleId: (r.BundleId as number) ?? null,
		BundleBarCount: (r.BundleBarCount as number) ?? null,
		BundleStartTime: r.BundleStartTime
			? moment.utc(r.BundleStartTime as string).toDate()
			: null,
		BundleEndTime: r.BundleEndTime
			? moment.utc(r.BundleEndTime as string).toDate()
			: null,
		MinLength: (r.MinLength as number) ?? null,
		MaxLength: (r.MaxLength as number) ?? null,
		IsCutback: (r.IsCutback as boolean) ?? null,
		CheckTL: (r.CheckTL as number) ?? null,
		CheckTR: (r.CheckTR as number) ?? null,
		CheckMC: (r.CheckMC as number) ?? null,
		CheckBL: (r.CheckBL as number) ?? null,
		CheckBR: (r.CheckBR as number) ?? null,
		CheckTime: moment.utc(r.CheckTime as string).toDate(),
		CreatedBy: (r.CreatedBy as string) ?? null,
	}));
};

export interface SawRecentBundle {
	BundleId: number;
	AssetId: number;
	StartTime: Date;
	EndTime: Date | null;
	BarCount: number | null;
	AvgLength: number | null;
	WorkOrderId: number | null;
	IsWorkOrderIdManual: boolean;
	IsReclassified: boolean;
	ReclassifiedToProductId: number | null;
	PartNumber: string | null;
	SectionName: string | null;
	GradeName: string | null;
	MinLength: number | null;
	MaxLength: number | null;
	IsCutback: boolean;
	HasCheck: boolean;
	CheckTL: number | null;
	CheckTR: number | null;
	CheckMC: number | null;
	CheckBL: number | null;
	CheckBR: number | null;
}

// bundleId pins one bundle even outside the recent window; top/daysBack widen it
export const getSawRecentBundles = async (
	assetId: number,
	bundleId: number | null = null,
	top: number | null = null,
	daysBack: number | null = null,
): Promise<SawRecentBundle[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "AssetId", value: assetId },
		{ name: "BundleId", value: bundleId },
	];
	if (top != null) parameters.push({ name: "Top", value: top });
	if (daysBack != null) parameters.push({ name: "DaysBack", value: daysBack });
	const resp = await apiService.callV2("[FAB].[GetRecentBundles]", parameters);
	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching recent bundles");
	}
	const rows = get(resp, "data.tables[0].rows", []) as Record<
		string,
		unknown
	>[];
	return rows.map(toRecentBundle);
};

export const toRecentBundle = (
	r: Record<string, unknown>,
): SawRecentBundle => ({
	BundleId: r.BundleId as number,
	AssetId: r.AssetId as number,
	StartTime: moment.utc(r.StartTime as string).toDate(),
	EndTime: r.EndTime ? moment.utc(r.EndTime as string).toDate() : null,
	BarCount: (r.BarCount as number) ?? null,
	AvgLength: (r.AvgLength as number) ?? null,
	WorkOrderId: (r.WorkOrderId as number) ?? null,
	IsWorkOrderIdManual: (r.IsWorkOrderIdManual as boolean) ?? false,
	IsReclassified: (r.IsReclassified as boolean) ?? false,
	ReclassifiedToProductId: (r.ReclassifiedToProductId as number) ?? null,
	PartNumber: (r.PartNumber as string) ?? null,
	SectionName: (r.SectionName as string) ?? null,
	GradeName: (r.GradeName as string) ?? null,
	MinLength: (r.MinLength as number) ?? null,
	MaxLength: (r.MaxLength as number) ?? null,
	IsCutback: r.IsCutback === true || r.IsCutback === 1,
	HasCheck: r.HasCheck === true || r.HasCheck === 1,
	CheckTL: (r.CheckTL as number) ?? null,
	CheckTR: (r.CheckTR as number) ?? null,
	CheckMC: (r.CheckMC as number) ?? null,
	CheckBL: (r.CheckBL as number) ?? null,
	CheckBR: (r.CheckBR as number) ?? null,
});

// Reclassify a bundle to another saw product (or Cutback). The SP records the
// new product and clears Bundles.WorkOrderId — reclassification never targets
// a work order.
export const reclassifyBundle = async (
	bundleId: number,
	productId: number | null,
	cutback = false,
	employeeNumber: string | null = null,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "BundleId", value: bundleId },
		{ name: "ProductId", value: productId },
		{ name: "Cutback", value: cutback },
		{ name: "EmployeeNumber", value: employeeNumber },
	];
	const resp = await apiService.callV2("[FAB].[ReclassifyBundle]", parameters);
	if (!resp.ok) {
		throw new Error(resp.message || "Error reclassifying bundle");
	}
	return resp;
};

// Undo: drops the reclassification and restores the original work order link.
export const revertBundleReclassification = async (
	bundleId: number,
	employeeNumber: string | null = null,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "BundleId", value: bundleId },
		{ name: "EmployeeNumber", value: employeeNumber },
	];
	const resp = await apiService.callV2(
		"[FAB].[RevertBundleReclassification]",
		parameters,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error reverting reclassification");
	}
	return resp;
};

// Row color: "current" wins when the tag matches both WOs; null = no running and no next WO.
export type WorkOrderMatch = "current" | "next" | "none" | null;

export interface SawEntryScanPreview {
	// scanned QR part or the resolved product; null for most mill tags
	PartNumber: string | null;
	Count: number;
	Limit: number;
	IsDuplicate: boolean;
	WorkOrderMatch: WorkOrderMatch;
	// a mill bundle is cut whole: loaded once, never again
	AlreadyLoadedAt: string | null;
}

const scanParameters = (
	assetId: number,
	tag: ParsedSawEntryBarcode,
): spParameter[] => [
	{ name: "AssetId", value: assetId },
	{ name: "TagNo", value: tag.tagNo },
	{ name: "SectionName", value: tag.sectionName },
	{ name: "GradeName", value: tag.gradeName },
	{ name: "MillLengthIn", value: tag.millLengthIn },
	{ name: "PartNumber", value: tag.partNumber },
];

export const getSawEntryScanPreview = async (
	assetId: number,
	tag: ParsedSawEntryBarcode,
): Promise<SawEntryScanPreview> => {
	const resp = await new MESApiService().callV2(
		"[FAB].[GetSawEntryScanPreview]",
		scanParameters(assetId, tag),
	);
	if (!resp.ok) throw new Error(resp.message || "Error checking the tag");
	const row = get(resp, "data.tables[0].rows[0]") as
		| SawEntryScanPreview
		| undefined;
	if (!row) throw new Error("Unable to check the tag. Try again.");
	return row;
};

// employee = tablet: the scan also joins the saw's material queue
export const upsertSawEntryScan = async (
	assetId: number,
	tag: ParsedSawEntryBarcode,
	employee?: string,
) => {
	const parameters: spParameter[] = [
		...scanParameters(assetId, tag),
		{ name: "HeatNo", value: tag.heatNo },
		{ name: "Pieces", value: tag.pieces },
		{ name: "RawBarcode", value: tag.raw },
	];
	if (employee)
		parameters.push(
			{ name: "AddToQueue", value: true },
			{ name: "EmployeeNumber", value: employee },
		);
	const resp = await new MESApiService().callV2(
		"[FAB].[UpsertSawEntryScan]",
		parameters,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error saving entry bundle scan");
	}
	return resp;
};
